import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtReevalNoteForm,
  updateOtReevalNoteForm,
} from "../../../../../services/patientService";
import {
  CommentsIcon,
  DeleteIcon,
  FileCheckIcon,
  HeartPulseIcon,
  LogOutIcon,
  MedicalNotesIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ASSESSMENT_AREA_ALIASES,
  ASSESSMENT_AREA_OPTIONS,
  ASSESSMENT_GROUPS,
  CHANGE_OPTIONS,
  CHANGES_SINCE_LAST_ALIASES,
  CHANGES_SINCE_LAST_OPTIONS,
  GOAL_STATUS_OPTIONS,
  PLAN_OF_CARE_STATUS_OPTIONS,
  OT_REEVAL_ARRAY_KEYS,
  REEVAL_REASON_OPTIONS,
  YES_NO_OPTIONS,
  emptyAssessmentFindings,
  emptyGoals,
  emptyOtReevalNotePayload,
  toggleInList,
  today,
} from "./otReevalNoteContent";
import {
  emptyProgressGoalRow,
  normalizeProgressGoals,
  renumberProgressGoals,
} from "../ptNotes/ptGoals";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);

const normalizeExclusiveYesNo = (value) => {
  if (Array.isArray(value)) {
    if (value.includes("Yes")) return "Yes";
    if (value.includes("No")) return "No";
    return "";
  }
  const s = String(value || "").trim();
  if (s === "Yes" || s === "No") return s;
  return "";
};

const normalizeAssessmentFindings = (rows) => {
  const findings = Array.isArray(rows) ? rows : [];
  const byArea = {};
  findings
    .filter((row) => row && row.area)
    .forEach((row) => {
      const area = ASSESSMENT_AREA_ALIASES[row.area] || row.area;
      byArea[area] = row;
    });
  return ASSESSMENT_AREA_OPTIONS.map((area) => ({
    area,
    lastAssessment: byArea[area]?.lastAssessment || "",
    currentAssessment: byArea[area]?.currentAssessment || "",
    change: byArea[area]?.change || "",
  }));
};

const normalizeChangesSinceLast = (list) => {
  const seen = new Set();
  const next = [];
  (Array.isArray(list) ? list : []).forEach((raw) => {
    const mapped = CHANGES_SINCE_LAST_ALIASES[raw] || raw;
    if (!CHANGES_SINCE_LAST_OPTIONS.includes(mapped) || seen.has(mapped)) {
      return;
    }
    seen.add(mapped);
    next.push(mapped);
  });
  return next;
};

const emptyValues = () => ({
  ...emptyOtReevalNotePayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const OtReevalNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(
    () => ({
      ...emptyOtReevalNotePayload(),
      assessmentFindings: emptyAssessmentFindings(),
      goals: emptyGoals(),
    }),
    [],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtReevalNotePayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        assessmentFindings: normalizeAssessmentFindings(
          loadedPayload.assessmentFindings,
        ),
        goals: normalizeProgressGoals(loadedPayload.goals, 3),
        additionalGoalsAttached: Boolean(loadedPayload.additionalGoalsAttached),
        skilledOtNeed: String(
          loadedPayload.skilledOtNeed || loadedPayload.skilledPtNeed || "",
        ).trim(),
        planOfCareComments: String(loadedPayload.planOfCareComments || "").trim(),
        dischargeAnticipated: normalizeExclusiveYesNo(
          loadedPayload.dischargeAnticipated,
        ),
        dischargeExpectedDate: String(
          loadedPayload.dischargeExpectedDate || "",
        ).trim(),
        dischargeNeedsPlan: String(loadedPayload.dischargeNeedsPlan || "").trim(),
        reevalReasonOther: String(loadedPayload.reevalReasonOther || "").trim(),
        changesComments: String(loadedPayload.changesComments || "").trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_REEVAL_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });
      merged.changesSinceLast = normalizeChangesSinceLast(
        merged.changesSinceLast,
      );

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Re-evaluation Note",
            formType: clinicalForm.formType || draft?.formType || "ot_reeval_note",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getOtReevalNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateAssessmentRow = (area, patch) => {
    setValues((prev) => ({
      ...prev,
      assessmentFindings: prev.assessmentFindings.map((row) =>
        row.area === area ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateGoalRow = (goalId, patch) => {
    setValues((prev) => ({
      ...prev,
      goals: prev.goals.map((row) =>
        row.id === goalId ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addGoalRow = () => {
    setValues((prev) => {
      const next = [
        ...(prev.goals || []),
        emptyProgressGoalRow((prev.goals || []).length + 1),
      ];
      return { ...prev, goals: renumberProgressGoals(next) };
    });
  };

  const removeGoalRow = (goalId) => {
    setValues((prev) => {
      const current = prev.goals || [];
      if (current.length <= 1) return prev;
      return {
        ...prev,
        goals: renumberProgressGoals(current.filter((row) => row.id !== goalId)),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType || "ot_reeval_note",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      visitDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...otFields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...otFields,
      visitDate: visitDate ? formatDateForInput(visitDate) : "",
      dischargeExpectedDate: values.dischargeExpectedDate
        ? formatDateForInput(values.dischargeExpectedDate)
        : "",
      assessmentFindings: normalizeAssessmentFindings(values.assessmentFindings),
      goals: renumberProgressGoals(values.goals).map((row) => ({
        id: row.id,
        label: row.label || "",
        goalText: row.goalText || "",
        status: row.status || "",
        evidence: row.evidence || "",
        continueGoal: row.continueGoal || "",
      })),
      additionalGoalsAttached: Boolean(values.additionalGoalsAttached),
      skilledOtNeed: String(values.skilledOtNeed || "").trim(),
      planOfCareComments: String(values.planOfCareComments || "").trim(),
      dischargeAnticipated: normalizeExclusiveYesNo(
        values.dischargeAnticipated,
      ),
      dischargeNeedsPlan: String(values.dischargeNeedsPlan || "").trim(),
      reevalReasonOther: String(values.reevalReasonOther || "").trim(),
      changesComments: String(values.changesComments || "").trim(),
    });

    OT_REEVAL_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtReevalNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showReevalReasonOther = (values.reevalReasons || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Re-evaluation Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            CMS re-evaluation documentation for skilled occupational therapy home
            health services.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date of Visit"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date of Visit
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Re-evaluation Information */}
        <Section icon={MedicalNotesIcon} title="7. Re-evaluation Information">
          <SubHeading>Reason for Re-Evaluation (Check all that apply)</SubHeading>
          <CheckboxGrid
            options={REEVAL_REASON_OPTIONS}
            values={values.reevalReasons}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("reevalReasons", opt)}
            columns={3}
          />
          {showReevalReasonOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.reevalReasonOther || ""}
              disabled={!canEdit}
              placeholder="Specify other reason…"
              onChange={(e) => setValue("reevalReasonOther", e.target.value)}
            />
          ) : null}
        </Section>

        {/* 8. Changes Since Last Assessment */}
        <Section icon={FileCheckIcon} title="8. Changes Since Last Assessment">
          <p className="text-[11px] text-slate-500 italic mb-2">
            Document significant changes since the last assessment.
          </p>
          <CheckboxGrid
            options={CHANGES_SINCE_LAST_OPTIONS}
            values={values.changesSinceLast}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("changesSinceLast", opt)}
            columns={3}
          />
          <div className="mt-3">
            <Field label="Other / Comments">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.changesComments || ""}
                disabled={!canEdit}
                placeholder="Additional comments on changes since last assessment…"
                onChange={(e) => setValue("changesComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 9. Current Occupational Therapy Assessment */}
        <Section
          icon={RunningIcon}
          title="9. Current Occupational Therapy Assessment (Compared to Last Assessment)"
        >
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2 w-[24%]">Functional Area</th>
                  <th className="px-2.5 py-2">Last Assessment</th>
                  <th className="px-2.5 py-2">Current Assessment</th>
                  <th className="px-2.5 py-2 w-[11rem]">
                    Change (Improved / Declined / No Change)
                  </th>
                </tr>
              </thead>
              <tbody>
                {ASSESSMENT_GROUPS.map((group) => (
                  <Fragment key={group.label}>
                    <tr className="bg-sky-50">
                      <td
                        colSpan={4}
                        className="px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wide text-sky-900"
                      >
                        {group.label}
                      </td>
                    </tr>
                    {group.areas.map((area) => {
                      const row = (values.assessmentFindings || []).find(
                        (item) => item.area === area,
                      ) || {
                        area,
                        lastAssessment: "",
                        currentAssessment: "",
                        change: "",
                      };
                      return (
                        <tr key={area} className="border-t border-slate-100">
                          <td className="px-2.5 py-1.5 font-medium text-slate-700 align-middle">
                            {area}
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              className={fieldClass}
                              value={row.lastAssessment}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateAssessmentRow(area, {
                                  lastAssessment: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              className={fieldClass}
                              value={row.currentAssessment}
                              disabled={!canEdit}
                              onChange={(e) =>
                                updateAssessmentRow(area, {
                                  currentAssessment: e.target.value,
                                })
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            {printMode || !canEdit ? (
                              <div className={`${fieldClass} flex items-center`}>
                                {row.change || "—"}
                              </div>
                            ) : (
                              <select
                                className={fieldClass}
                                value={row.change || ""}
                                onChange={(e) =>
                                  updateAssessmentRow(area, {
                                    change: e.target.value,
                                  })
                                }
                              >
                                <option value="">—</option>
                                {CHANGE_OPTIONS.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 10. Progress Toward Goals */}
        <Section icon={TargetIcon} title="10. Progress Toward Goals">
          <p className="text-[11px] text-slate-500 italic mb-2">
            Indicate progress toward goals established in the Plan of Care.
          </p>
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="min-w-full text-left text-[11px]">
              <thead>
                <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-2.5 py-2 w-[28%]">Goal</th>
                  <th className="px-2.5 py-2">Status</th>
                  <th className="px-2.5 py-2">Evidence / Measurement</th>
                  <th className="px-2.5 py-2 w-[7.5rem]">Continue?</th>
                  {canEdit ? (
                    <th className="px-2.5 py-2 w-10 text-right"> </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {(values.goals || []).map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 align-top">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                        {row.label}
                      </div>
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.goalText || ""}
                        disabled={!canEdit}
                        placeholder="Enter goal…"
                        onChange={(e) =>
                          updateGoalRow(row.id, { goalText: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-2 align-middle">
                      <div className="flex flex-col gap-1.5">
                        {GOAL_STATUS_OPTIONS.map((opt) => (
                          <label
                            key={opt}
                            className={`inline-flex items-center gap-1.5 text-[11px] text-slate-700 ${
                              canEdit ? "cursor-pointer" : "cursor-default"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`${row.id}-status`}
                              className="h-3.5 w-3.5 border-slate-300 text-indigo-500"
                              checked={row.status === opt}
                              disabled={!canEdit}
                              onChange={() =>
                                updateGoalRow(row.id, { status: opt })
                              }
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      <textarea
                        rows={3}
                        className={textareaClass}
                        value={row.evidence}
                        disabled={!canEdit}
                        placeholder="Evidence / measurement…"
                        onChange={(e) =>
                          updateGoalRow(row.id, { evidence: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-2 py-2 align-middle">
                      <div className="flex flex-col gap-1.5">
                        {YES_NO_OPTIONS.map((opt) => (
                          <label
                            key={opt}
                            className={`inline-flex items-center gap-1.5 text-[11px] text-slate-700 ${
                              canEdit ? "cursor-pointer" : "cursor-default"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`${row.id}-continue`}
                              className="h-3.5 w-3.5 border-slate-300 text-indigo-500"
                              checked={row.continueGoal === opt}
                              disabled={!canEdit}
                              onChange={() =>
                                updateGoalRow(row.id, { continueGoal: opt })
                              }
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </td>
                    {canEdit ? (
                      <td className="px-2 py-2 align-middle text-right">
                        <button
                          type="button"
                          disabled={(values.goals || []).length <= 1}
                          onClick={() => removeGoalRow(row.id)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400"
                          aria-label={`Remove ${row.label}`}
                        >
                          <DeleteIcon size={14} />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <label
              className={`inline-flex items-center gap-2 text-[11px] text-slate-700 ${
                canEdit ? "cursor-pointer" : "cursor-default"
              }`}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-500"
                checked={Boolean(values.additionalGoalsAttached)}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("additionalGoalsAttached", e.target.checked)
                }
              />
              <span>Additional Goals Attached</span>
            </label>
            {canEdit ? (
              <button
                type="button"
                onClick={addGoalRow}
                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/50"
              >
                <PlusIcon size={13} />
                Add Goal
              </button>
            ) : null}
          </div>
        </Section>

        {/* 11. Skilled OT Need / Medical Necessity */}
        <Section
          icon={HeartPulseIcon}
          title="11. Skilled OT Need / Medical Necessity"
        >
          <SubHeading>
            Patient continues to require skilled occupational therapy to:
          </SubHeading>
          <textarea
            rows={3}
            className={`${textareaClass} mt-1.5`}
            value={values.skilledOtNeed || ""}
            disabled={!canEdit}
            placeholder="Document ongoing skilled OT need and medical necessity…"
            onChange={(e) => setValue("skilledOtNeed", e.target.value)}
          />
        </Section>

        {/* 12. Plan of Care / Orders */}
        <Section icon={CommentsIcon} title="12. Plan of Care / Orders">
          <div>
            <SubHeading>Plan of Care Status</SubHeading>
            <CheckboxGrid
              options={PLAN_OF_CARE_STATUS_OPTIONS}
              values={values.planOfCareStatus}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("planOfCareStatus", opt)}
              columns={3}
            />
          </div>
          <div className="mt-3">
            <Field label="Comments / New Orders">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.planOfCareComments || ""}
                disabled={!canEdit}
                placeholder="Describe plan changes, new orders, or frequency adjustments…"
                onChange={(e) => setValue("planOfCareComments", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        {/* 13. Discharge Planning */}
        <Section icon={LogOutIcon} title="13. Discharge Planning">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
            <div>
              <SubHeading>Discharge Anticipated?</SubHeading>
              <ExclusiveChoiceGrid
                name="dischargeAnticipated"
                options={YES_NO_OPTIONS}
                value={values.dischargeAnticipated || ""}
                canEdit={canEdit}
                onChange={(opt) => {
                  setValue("dischargeAnticipated", opt);
                  if (opt !== "Yes") setValue("dischargeExpectedDate", "");
                }}
                columns={2}
              />
            </div>
            <Field label="If Yes, Expected Date">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.dischargeExpectedDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="dischargeExpectedDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit || values.dischargeAnticipated !== "Yes"}
                  value={
                    formatDateForInput(values.dischargeExpectedDate) || ""
                  }
                  onChange={(event) =>
                    setValue("dischargeExpectedDate", event.target.value)
                  }
                  className="w-full"
                  inputClassName={`${fieldClass} bg-white`}
                />
              )}
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Discharge Needs / Plan">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.dischargeNeedsPlan || ""}
                disabled={!canEdit}
                placeholder="Document anticipated discharge needs and plan…"
                onChange={(e) => setValue("dischargeNeedsPlan", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Occupational Therapist"
          patientSectionTitle="Patient / Caregiver (Acknowledgement of Visit)"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtReevalNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtReevalNoteForm;
